(function () {
  const DESKTOP_MQ = window.matchMedia('(min-width: 1141px)');
  const MENU_MODAL_NAME = 'header-humm-menu';
  const CART_MODAL_NAME = 'cart';
  const LEGACY_CART_SECTION_ID = 'cart-drawer';
  const ROOT = document.documentElement;
  const CART_CHROME_CLASS = 'header-humm--cart-chrome';
  const CHROME_MEASURE_FOLLOWUP_MS = 120;

  let headerObserver = null;
  let stickyObserver = null;
  let modalObserver = null;
  let resizeTimer = null;
  let chromeMeasureTimer = null;
  let openAnchorTimers = [];
  let bodyClassObserver = null;
  let reopenCartAfterEditorReload = false;

  function getCartMiniEl() {
    return document.querySelector('xo-cart-mini');
  }

  function getCartDrawerSectionId() {
    return getCartMiniEl()?.id || '';
  }

  function rewriteCartDrawerSections(value, sectionId) {
    if (!value || !sectionId) return value;

    if (Array.isArray(value)) {
      return value.map((entry) => (entry === LEGACY_CART_SECTION_ID ? sectionId : entry));
    }

    if (typeof value === 'string') {
      return value
        .split(',')
        .map((entry) => entry.trim())
        .map((entry) => (entry === LEGACY_CART_SECTION_ID ? sectionId : entry))
        .join(',');
    }

    return value;
  }

  function patchCartSectionsBody(body, sectionId) {
    if (!body || !sectionId) return body;

    if (body instanceof FormData) {
      const sections = body.get('sections');
      if (typeof sections === 'string') {
        body.set('sections', rewriteCartDrawerSections(sections, sectionId));
      }
      return body;
    }

    if (typeof body === 'string') {
      try {
        const parsed = JSON.parse(body);
        if (parsed.sections != null) {
          parsed.sections = rewriteCartDrawerSections(parsed.sections, sectionId);
          return JSON.stringify(parsed);
        }
      } catch (error) {
        return body;
      }
    }

    return body;
  }

  function isCartMutationRequest(url) {
    try {
      const pathname = new URL(url, window.location.href).pathname;
      return /\/cart\/(add|change|update)(\.js)?$/.test(pathname);
    } catch (error) {
      return false;
    }
  }

  function remapCartSectionsResponse(data, sectionId) {
    if (!data?.sections || !sectionId) return data;

    const legacyHtml = data.sections[LEGACY_CART_SECTION_ID];
    if (legacyHtml && !data.sections[sectionId]) {
      data.sections[sectionId] = legacyHtml;
      delete data.sections[LEGACY_CART_SECTION_ID];
    }

    return data;
  }

  function parseDiscountUpdateBody(body) {
    if (!body) return null;
    if (typeof body === 'string') {
      try {
        const parsed = JSON.parse(body);
        return typeof parsed.discount === 'string' ? parsed.discount : null;
      } catch (error) {
        return null;
      }
    }
    return null;
  }

  function escapeDiscountCodeAttr(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;');
  }

  function injectHummCartDiscountItems(sectionHtml, discountCodes) {
    if (!sectionHtml || !Array.isArray(discountCodes) || !discountCodes.length) return sectionHtml;
    if (!sectionHtml.includes('<xo-cart-discount')) return sectionHtml;

    const applicableCodes = discountCodes
      .filter((entry) => entry.applicable)
      .map((entry) => entry.code)
      .filter(Boolean);

    const missingCodes = applicableCodes.filter(
      (code) => !sectionHtml.includes(`xo-discount-code="${escapeDiscountCodeAttr(code)}"`)
    );

    if (!missingCodes.length) return sectionHtml;

    const markers = missingCodes
      .map(
        (code) =>
          `<xo-cart-discount-item xo-discount-code="${escapeDiscountCodeAttr(code)}" data-humm-discount-marker hidden></xo-cart-discount-item>`
      )
      .join('');

    return sectionHtml.replace(/<xo-cart-discount(\s[^>]*)?>/i, (match) => `${match}${markers}`);
  }

  function installCartSectionFetchPatch() {
    if (window.__hummCartSectionFetchPatched) return;

    const previousFetch = window.fetch.bind(window);

    window.fetch = async (input, init = {}) => {
      const sectionId = getCartDrawerSectionId();
      const url = typeof input === 'string' ? input : input?.url;
      let nextInit = init;
      const discountSent = isCartMutationRequest(url) ? parseDiscountUpdateBody(init.body) : null;

      if (sectionId && isCartMutationRequest(url) && init.body) {
        nextInit = { ...init, body: patchCartSectionsBody(init.body, sectionId) };
      }

      const response = await previousFetch(input, nextInit);

      if (!sectionId || !isCartMutationRequest(url)) {
        return response;
      }

      try {
        const data = await response.clone().json();
        const hadLegacySection = Boolean(data.sections?.[LEGACY_CART_SECTION_ID]);
        remapCartSectionsResponse(data, sectionId);

        let responseMutated = hadLegacySection;

        if (discountSent && sectionId && data.sections?.[sectionId]) {
          const sectionHtmlBefore = data.sections[sectionId];
          data.sections[sectionId] = injectHummCartDiscountItems(
            sectionHtmlBefore,
            data.discount_codes
          );

          if (data.sections[sectionId] !== sectionHtmlBefore) {
            responseMutated = true;
          }
        }

        if (responseMutated) {
          return new Response(JSON.stringify(data), {
            status: response.status,
            statusText: response.statusText,
            headers: response.headers,
          });
        }
      } catch (error) {
        return response;
      }

      return response;
    };

    window.__hummCartSectionFetchPatched = true;
  }

  function isCartSectionElement(sectionEl) {
    return Boolean(sectionEl?.classList?.contains('section-xo-cart-mini'));
  }

  function getCartModal() {
    return document.querySelector('xo-modal[xo-name="cart"]');
  }

  function isModalOpen(name) {
    const modal = document.querySelector(`xo-modal[xo-name="${name}"]`);
    return Boolean(modal?.hasAttribute('xo-active') && modal.getAttribute('xo-active') !== 'false');
  }

  function isCartOpenInStore() {
    return Boolean(window.xoStore?.get?.('xo-modal')?.data?.[CART_MODAL_NAME]?.isOpen);
  }

  function closeModal(name) {
    if (typeof window.xoModal?.close === 'function') {
      window.xoModal.close(name);
    }
  }

  function closeMenuDrawer() {
    if (!DESKTOP_MQ.matches && isModalOpen(MENU_MODAL_NAME)) {
      closeModal(MENU_MODAL_NAME);
    }
  }

  function getCartTriggers() {
    return Array.from(document.querySelectorAll('.header-humm__cart'));
  }

  function getVisibleCartTrigger() {
    const triggers = getCartTriggers();
    return triggers.find((el) => el.offsetParent !== null || el.getClientRects().length) || triggers[0];
  }

  function getHeader() {
    return document.querySelector('.header-humm');
  }

  function getHeaderStickyElement(header = getHeader()) {
    return header?.querySelector('xo-sticky') || null;
  }

  function isHeaderScrollSticky(header = getHeader()) {
    const sticky = getHeaderStickyElement(header);
    if (!sticky) return false;

    return sticky.hasAttribute('xo-is-sticky') && sticky.getAttribute('xo-is-sticky') !== 'false';
  }

  function getHeaderAnchorElement(header) {
    return header.querySelector('.header-humm__container') || header.querySelector('.header-humm__body') || header;
  }

  function dispatchHeaderChromeChange() {
    document.dispatchEvent(new CustomEvent('humm:header-chrome-change'));
  }

  function getAnchorRight(rectRight) {
    return Math.round(document.documentElement.clientWidth - rectRight);
  }

  function getDrawerWidthPx() {
    const drawer = document.querySelector('[data-cart-drawer-humm]');
    if (drawer) {
      const measured = Math.round(drawer.offsetWidth || drawer.getBoundingClientRect().width);
      if (measured > 0) return measured;
    }

    const widthSource = drawer ? getComputedStyle(drawer).getPropertyValue('--hcd-width').trim() : '';
    const rootWidth = getComputedStyle(ROOT).getPropertyValue('--hcd-width').trim();
    const remValue = parseFloat(widthSource || rootWidth) || 54;
    const htmlFont = parseFloat(getComputedStyle(ROOT).fontSize) || 16;
    return Math.round(remValue * htmlFont);
  }

  function alignDrawerRightEdge(header) {
    const drawer = document.querySelector('[data-cart-drawer-humm]');
    const anchor = getHeaderAnchorElement(header);
    if (!drawer || !anchor || !DESKTOP_MQ.matches) return;

    const targetRight = Math.round(anchor.getBoundingClientRect().right);
    const drawerRight = Math.round(drawer.getBoundingClientRect().right);
    const delta = targetRight - drawerRight;

    if (!Number.isFinite(delta) || Math.abs(delta) <= 1) return;

    const currentLeft = parseFloat(getComputedStyle(drawer).left);
    if (!Number.isFinite(currentLeft)) return;

    ROOT.style.setProperty('--hcd-anchor-left', `${Math.round(currentLeft + delta)}px`);
    ROOT.style.setProperty('--hcd-drawer-right-edge', `${targetRight}px`);
  }

  function clearOpenAnchorTimers() {
    openAnchorTimers.forEach((id) => window.clearTimeout(id));
    openAnchorTimers = [];
  }

  function scheduleOpenAnchorSync() {
    clearOpenAnchorTimers();

    const run = () => {
      lockStickyHeaderTop(true);
      measure();
      const header = getHeader();
      if (header) alignDrawerRightEdge(header);
    };
    run();

    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(run);
    });

    [50, 120, 450].forEach((delay) => {
      openAnchorTimers.push(window.setTimeout(run, delay));
    });
  }

  function readStickyInnerTop(sticky) {
    const inner = sticky?.shadowRoot?.querySelector('xo-sticky-inner');
    if (inner) {
      const topPx = Math.round(inner.getBoundingClientRect().top);
      if (Number.isFinite(topPx)) return `${topPx}px`;
    }

    if (!sticky) return '';

    try {
      const computedTop = getComputedStyle(sticky, '::part(inner)').top;
      if (computedTop && computedTop !== 'auto') return computedTop;
    } catch (error) {
      /* ::part computed style unavailable */
    }

    return '';
  }

  function freezeStickyHeaderGeometry() {
    const header = getHeader();
    if (!header || !DESKTOP_MQ.matches || !isHeaderScrollSticky(header)) return false;

    const sticky = getHeaderStickyElement(header);
    const lockedTop = readStickyInnerTop(sticky);

    if (!lockedTop) return false;

    ROOT.style.setProperty('--hcd-sticky-inner-top-lock', lockedTop);
    header.classList.add('header-humm--cart-anchor-lock');
    return true;
  }

  function lockStickyHeaderTop(isOpen) {
    const header = getHeader();
    if (!header || !DESKTOP_MQ.matches) return;

    if (!isOpen || !isHeaderScrollSticky(header)) {
      header.classList.remove('header-humm--cart-anchor-lock');
      ROOT.style.removeProperty('--hcd-sticky-inner-top-lock');
      return;
    }

    freezeStickyHeaderGeometry();
  }

  function syncCartChrome(isOpen) {
    const header = getHeader();
    if (!header || !DESKTOP_MQ.matches) return false;

    const scrollSticky = isHeaderScrollSticky(header);

    if (scrollSticky) {
      if (header.classList.contains(CART_CHROME_CLASS)) {
        header.classList.remove(CART_CHROME_CLASS);
        dispatchHeaderChromeChange();
        return true;
      }
      return false;
    }

    const shouldHaveChrome = Boolean(isOpen);
    const hasChrome = header.classList.contains(CART_CHROME_CLASS);

    if (shouldHaveChrome === hasChrome) return false;

    header.classList.toggle(CART_CHROME_CLASS, shouldHaveChrome);
    dispatchHeaderChromeChange();
    return true;
  }

  function scheduleChromeFollowupMeasure() {
    window.clearTimeout(chromeMeasureTimer);
    chromeMeasureTimer = window.setTimeout(measure, CHROME_MEASURE_FOLLOWUP_MS);
  }

  function scheduleMeasureAfterChromeChange() {
    scheduleMeasure();
    window.requestAnimationFrame(() => {
      measure();
      scheduleChromeFollowupMeasure();
    });
  }

  function setExpandedState(isOpen) {
    getCartTriggers().forEach((trigger) => {
      const cartLabel = trigger.dataset.cartLabel || trigger.getAttribute('aria-label') || 'Cart';
      const closeCartLabel = trigger.dataset.closeCartLabel || 'Close cart';

      trigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      trigger.setAttribute('aria-label', isOpen ? closeCartLabel : cartLabel);
    });
    document.body.classList.toggle('cart-drawer-humm-open', isOpen);
  }

  function syncOpenState() {
    const modal = getCartModal();
    const isOpen = modal?.hasAttribute('xo-active') && modal.getAttribute('xo-active') !== 'false';

    if (isOpen && !DESKTOP_MQ.matches && modal?.hasAttribute('popover')) {
      modal.removeAttribute('popover');
    }

    if (isOpen) {
      closeMenuDrawer();
      lockStickyHeaderTop(true);
    }

    setExpandedState(Boolean(isOpen));
    const chromeChanged = syncCartChrome(isOpen);
    scheduleMeasure();

    if (isOpen) {
      scheduleOpenAnchorSync();
    } else {
      clearOpenAnchorTimers();
      lockStickyHeaderTop(false);
      if (chromeChanged) {
        scheduleChromeFollowupMeasure();
      }
    }

    return Boolean(isOpen);
  }

  function resetCartModalState() {
    if (typeof window.xoModal?.close === 'function') {
      window.xoModal.close(CART_MODAL_NAME, 'empty');
    }
    setExpandedState(false);
    clearOpenAnchorTimers();
    const header = getHeader();
    if (header) {
      header.classList.remove('header-humm--cart-anchor-lock');
    }
    ROOT.style.removeProperty('--hcd-sticky-inner-top-lock');
    syncCartChrome(false);
    dispatchHeaderChromeChange();
  }

  function reopenCartInThemeEditor() {
    if (!window.Shopify?.designMode || !reopenCartAfterEditorReload) return;

    resetCartModalState();

    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        const trigger = getVisibleCartTrigger();
        if (typeof window.xoModal?.open === 'function') {
          window.xoModal.open(CART_MODAL_NAME, trigger);
        }
        syncOpenState();
        reopenCartAfterEditorReload = false;
      });
    });
  }

  function getHeaderBottomEdge(selector) {
    const element = document.querySelector(selector);
    if (!element || element.classList.contains('is-dismissed')) return 0;

    const rect = element.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0 && rect.bottom > 0 ? rect.bottom : 0;
  }

  function measureMobileHeaderOffset(header) {
    const drawerOffset = header ? getComputedStyle(header).getPropertyValue('--hh-drawer-offset').trim() : '';
    if (drawerOffset) {
      ROOT.style.setProperty('--hcd-mobile-header-height', drawerOffset);
      return;
    }

    const announcementBottom = getHeaderBottomEdge('#xo-announcement-bar');
    const mobileHeaderBottom = getHeaderBottomEdge('.header-humm__mobile');
    const offset = Math.ceil(Math.max(announcementBottom, mobileHeaderBottom) + 24);
    ROOT.style.setProperty('--hcd-mobile-header-height', `${Math.max(offset, 80)}px`);
  }

  function measure() {
    const header = getHeader();
    if (!header) return;

    if (DESKTOP_MQ.matches) {
      const anchor = getHeaderAnchorElement(header);
      const rect = anchor.getBoundingClientRect();
      const styles = window.getComputedStyle(anchor);

      const anchorRight = getAnchorRight(rect.right);
      const rightEdge = Math.round(rect.right);
      const anchorLeft = rightEdge - getDrawerWidthPx();

      ROOT.style.setProperty('--hcd-anchor-top', `${Math.round(rect.top)}px`);
      ROOT.style.setProperty('--hcd-anchor-right', `${anchorRight}px`);
      ROOT.style.setProperty('--hcd-anchor-left', `${anchorLeft}px`);
      ROOT.style.setProperty('--hcd-drawer-right-edge', `${rightEdge}px`);
      ROOT.style.setProperty('--hcd-anchor-bottom', `${Math.round(rect.top)}px`);
      ROOT.style.setProperty('--hcd-header-height', `${Math.round(rect.height)}px`);

      const rawRadius = parseFloat(styles.borderTopRightRadius) || 0;
      const effectiveRadius = Math.min(rawRadius, rect.height / 2);
      ROOT.style.setProperty('--hcd-header-radius', `${Math.round(effectiveRadius)}px`);

      const trigger = getVisibleCartTrigger();
      if (trigger) {
        const triggerRect = trigger.getBoundingClientRect();
        ROOT.style.setProperty('--hcd-trigger-size', `${Math.round(Math.max(triggerRect.width, triggerRect.height))}px`);
      }

      if (document.body.classList.contains('cart-drawer-humm-open')) {
        alignDrawerRightEdge(header);
      }
    } else {
      measureMobileHeaderOffset(header);
    }

    ROOT.classList.add('cart-drawer-humm-ready');
  }

  function scheduleMeasure() {
    if (resizeTimer) window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(measure, 16);
  }

  function observeHeader() {
    const header = document.querySelector('.header-humm');
    if (!header) return;

    if (headerObserver) headerObserver.disconnect();
    headerObserver = new ResizeObserver(scheduleMeasure);
    headerObserver.observe(header);

    const sticky = getHeaderStickyElement(header);
    if (sticky) {
      if (stickyObserver) stickyObserver.disconnect();
      stickyObserver = new MutationObserver(() => {
        if (isHeaderScrollSticky(header) && header.classList.contains(CART_CHROME_CLASS)) {
          header.classList.remove(CART_CHROME_CLASS);
          dispatchHeaderChromeChange();
        }
        scheduleMeasure();
      });
      stickyObserver.observe(sticky, { attributes: true, attributeFilter: ['xo-is-sticky', 'class', 'style'] });
    }
  }

  function observeCartModal() {
    const modal = getCartModal();
    if (!modal) return;

    if (modalObserver) modalObserver.disconnect();
    modalObserver = new MutationObserver(() => {
      syncOpenState();
    });
    modalObserver.observe(modal, { attributes: true, attributeFilter: ['xo-active'] });
    syncOpenState();
  }

  function observeBodyModalClass() {
    if (bodyClassObserver) bodyClassObserver.disconnect();

    bodyClassObserver = new MutationObserver(() => {
      if (!document.body.classList.contains('cart-drawer-humm-open')) return;
      scheduleMeasure();
    });

    bodyClassObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });
  }

  function init() {
    installCartSectionFetchPatch();
    measure();
    observeHeader();
    observeCartModal();
    observeBodyModalClass();

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', scheduleMeasure, { passive: true });
    }

    document.addEventListener(
      'click',
      (event) => {
        if (DESKTOP_MQ.matches && event.target.closest('.header-humm__cart')) {
          freezeStickyHeaderGeometry();
          return;
        }

        if (!event.target.closest('.header-humm__cart') || DESKTOP_MQ.matches) return;
        closeMenuDrawer();
        scheduleMeasure();
      },
      true
    );

    document.addEventListener(
      'pointerdown',
      (event) => {
        if (!DESKTOP_MQ.matches || !event.target.closest('.header-humm__cart')) return;
        freezeStickyHeaderGeometry();
      },
      true
    );

    document.addEventListener(
      'mouseup',
      () => {
        window.requestAnimationFrame(() => {
          if (!document.body.classList.contains('cart-drawer-humm-open')) {
            lockStickyHeaderTop(false);
          }
        });
      },
      true
    );

    window.addEventListener('resize', scheduleMeasure, { passive: true });
    window.addEventListener('scroll', scheduleMeasure, { passive: true });

    document.addEventListener('shopify:section:select', (event) => {
      if (!isCartSectionElement(event.target)) return;
      reopenCartAfterEditorReload = true;
    });

    document.addEventListener('shopify:section:deselect', (event) => {
      if (!isCartSectionElement(event.target)) return;
      reopenCartAfterEditorReload = false;
    });

    document.addEventListener('shopify:section:unload', (event) => {
      if (!isCartSectionElement(event.target)) return;
      if (window.Shopify?.designMode) {
        reopenCartAfterEditorReload =
          reopenCartAfterEditorReload || isModalOpen(CART_MODAL_NAME) || isCartOpenInStore();
      }
      resetCartModalState();
    });

    document.addEventListener('shopify:section:load', (event) => {
      const isCartSection = isCartSectionElement(event.target);

      scheduleMeasure();
      observeHeader();
      observeCartModal();

      if (isCartSection) {
        reopenCartInThemeEditor();
      }
    });

    if (typeof DESKTOP_MQ.addEventListener === 'function') {
      DESKTOP_MQ.addEventListener('change', () => {
        const header = getHeader();
        if (!DESKTOP_MQ.matches && header?.classList.contains(CART_CHROME_CLASS)) {
          header.classList.remove(CART_CHROME_CLASS);
          dispatchHeaderChromeChange();
        }
        scheduleMeasure();
      });
    } else {
      DESKTOP_MQ.addListener(() => {
        const header = getHeader();
        if (!DESKTOP_MQ.matches && header?.classList.contains(CART_CHROME_CLASS)) {
          header.classList.remove(CART_CHROME_CLASS);
          dispatchHeaderChromeChange();
        }
        scheduleMeasure();
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/*
  HUMM cart drawer — additive reference-design enhancement.
  IMPORTANT: append this to the END of your existing cart drawer JS.
  It does not replace, remove, or override your existing cart logic.

  What it does:
  1. Inserts the three trust callouts immediately above the existing checkout block.
  2. Adds a secure-checkout styling hook to the existing checkout button/link.
  3. Re-applies only the visual enhancement after Shopify/XO cart re-renders.
*/

(() => {
  const TRUST_CLASS = 'cart-drawer-humm-trust';
  const SECURE_BUTTON_CLASS = 'cart-drawer-humm-checkout__button--secure';
  const ENHANCED_ATTR = 'data-humm-secure-checkout-enhanced';

  const trustMarkup = `
    <div class="${TRUST_CLASS}" aria-label="Checkout benefits">
      <div class="cart-drawer-humm-trust__item">
        <span class="cart-drawer-humm-trust__icon" aria-hidden="true">
          <svg viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="7" y="13" width="18" height="15" rx="2" stroke="currentColor" stroke-width="2"/>
            <path d="M11 13V9C11 6.23858 13.2386 4 16 4C18.7614 4 21 6.23858 21 9V13" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            <path d="M16 19V22" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          </svg>
        </span>
        <span class="cart-drawer-humm-trust__copy">
          <strong>Secure Checkout</strong>
          <small>256-bit encryption</small>
        </span>
      </div>

      <div class="cart-drawer-humm-trust__item">
        <span class="cart-drawer-humm-trust__icon" aria-hidden="true">
          <svg viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M8.2 10.2H3.8V5.8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M4.3 9.8C6.7 5.9 11 3.5 16 3.5C23.5 3.5 29.5 9.5 29.5 17C29.5 24.5 23.5 30.5 16 30.5C10 30.5 4.9 26.6 3.1 21.2" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          </svg>
        </span>
        <span class="cart-drawer-humm-trust__copy">
          <strong>Easy Returns</strong>
          <small>30-day returns</small>
        </span>
      </div>

      <div class="cart-drawer-humm-trust__item">
        <span class="cart-drawer-humm-trust__icon" aria-hidden="true">
          <svg viewBox="0 0 36 32" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M3 7H23V23H3V7Z" stroke="currentColor" stroke-width="2" stroke-linejoin="round"/>
            <path d="M23 12H29L33 17V23H23V12Z" stroke="currentColor" stroke-width="2" stroke-linejoin="round"/>
            <circle cx="9" cy="24" r="3" fill="white" stroke="currentColor" stroke-width="2"/>
            <circle cx="28" cy="24" r="3" fill="white" stroke="currentColor" stroke-width="2"/>
          </svg>
        </span>
        <span class="cart-drawer-humm-trust__copy">
          <strong>Free Shipping</strong>
          <small>On all orders</small>
        </span>
      </div>
    </div>
  `;

  function getCheckoutButton(checkout) {
    if (!checkout) return null;

    if (checkout.matches('button, a, input[type="submit"], [role="button"]')) {
      return checkout;
    }

    return (
      checkout.querySelector('a[href*="/checkout"]') ||
      checkout.querySelector('button[type="submit"]') ||
      checkout.querySelector('input[type="submit"]') ||
      checkout.querySelector('.button') ||
      checkout.querySelector('button') ||
      checkout.querySelector('a') ||
      checkout.querySelector('[role="button"]')
    );
  }

  function updateSimpleCheckoutLabel(button) {
    if (!button || button.hasAttribute(ENHANCED_ATTR)) return;

    button.setAttribute(ENHANCED_ATTR, 'true');
    button.classList.add(SECURE_BUTTON_CLASS);
    button.setAttribute('aria-label', 'Check out securely');

    if (button.matches('input[type="submit"]')) {
      if (/check\s*out/i.test(button.value || '')) {
        button.value = 'CHECK OUT SECURELY';
      }
      return;
    }

    let possibleLabel =
      button.querySelector('[data-button-text]') ||
      button.querySelector('.button__label') ||
      button.querySelector('.xo-button__text');

    if (
      !possibleLabel &&
      button.children.length === 1 &&
      button.firstElementChild &&
      button.firstElementChild.tagName === 'SPAN'
    ) {
      possibleLabel = button.firstElementChild;
    }

    const labelTarget = possibleLabel || button;

    /*
      Only replace a simple text-only label that already says Checkout/Check out.
      Nested loaders/icons are left untouched, so checkout/loading behavior is preserved.
    */
    if (
      labelTarget.children.length === 0 &&
      /check\s*out/i.test(labelTarget.textContent || '')
    ) {
      labelTarget.textContent = 'CHECK OUT SECURELY';
    }
  }

  function enhanceDrawer(drawer) {
    const checkout = drawer.querySelector('.cart-drawer-humm-checkout');
    if (!checkout) return;

    let trust = drawer.querySelector(`.${TRUST_CLASS}`);
    if (!trust) {
      checkout.insertAdjacentHTML('beforebegin', trustMarkup);
      trust = checkout.previousElementSibling;
    } else if (trust.nextElementSibling !== checkout) {
      checkout.before(trust);
    }

    const checkoutButton = getCheckoutButton(checkout);
    updateSimpleCheckoutLabel(checkoutButton);
  }

  function enhanceAllDrawers() {
    document.querySelectorAll('.cart-drawer-humm').forEach(enhanceDrawer);
  }

  let queued = false;
  function queueEnhancement() {
    if (queued) return;
    queued = true;
    queueMicrotask(() => {
      queued = false;
      enhanceAllDrawers();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', enhanceAllDrawers, { once: true });
  } else {
    enhanceAllDrawers();
  }

  const observer = new MutationObserver(queueEnhancement);
  observer.observe(document.documentElement, {
    childList: true,
    subtree: true
  });
})();
